import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import usePermisos from '../hooks/usePermisos';

const EMPTY_FORM = {
    nombre: '', apellido: '', email: '', password: '',
    cedula: '', telefono: '', zona: '', role_id: '', fecha_expiracion: ''
};

const Users = () => {
    const { can, loaded } = usePermisos();
    const [users, setUsers] = useState([]);
    const [roles, setRoles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(EMPTY_FORM);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [search, setSearch] = useState('');
    const [saving, setSaving] = useState(false);

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const [usersRes, rolesRes] = await Promise.all([
                api.get('/users'),
                api.get('/roles')
            ]);
            setUsers(usersRes.data);
            setRoles(rolesRes.data);
        } catch (err) {
            if (err.response?.status !== 403) {
                setError('Error cargando datos');
            }
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { 
        if (loaded) loadData(); 
    }, [loadData, loaded]);

    const showSuccess = (msg) => {
        setSuccess(msg); setError('');
        setTimeout(() => setSuccess(''), 4000);
    };

    const openCreate = () => {
        setEditing(null);
        setForm(EMPTY_FORM);
        setError('');
        setModalOpen(true);
    };

    const openEdit = (user) => {
        setEditing(user);
        setForm({
            nombre: user.nombre,
            apellido: user.apellido,
            email: user.email,
            password: '',
            cedula: user.cedula || '',
            telefono: user.telefono || '',
            zona: user.zona || '',
            role_id: user.role_id || '',
            estado: user.estado,
            fecha_expiracion: user.fecha_expiracion ? user.fecha_expiracion.substring(0, 10) : ''
        });
        setError('');
        setModalOpen(true);
    };

    const handleChange = (e) => {
        setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const handleSubmit = async () => {
        setSaving(true);
        setError('');
        try {
            if (editing) {
                await api.put(`/users/${editing.uuid}`, form);
                showSuccess('Usuario actualizado correctamente');
            } else {
                await api.post('/users', form);
                showSuccess('Usuario creado correctamente');
            }
            setModalOpen(false);
            loadData();
        } catch (err) {
            setError(err.response?.data?.error || 'Error guardando usuario');
        } finally {
            setSaving(false);
        }
    };

    const handleDeactivate = async (uuid) => {
        if (!window.confirm('¿Desactivar este usuario?')) return;
        try {
            await api.delete(`/users/${uuid}`);
            showSuccess('Usuario desactivado');
            loadData();
        } catch (err) {
            setError(err.response?.data?.error || 'Error desactivando usuario');
        }
    };

    const filtered = users.filter(u =>
        `${u.nombre} ${u.apellido} ${u.email} ${u.cedula}`.toLowerCase().includes(search.toLowerCase())
    );

    const estadoColor = {
        activo: 'bg-green-100 text-green-700',
        inactivo: 'bg-gray-100 text-gray-500',
        suspendido: 'bg-red-100 text-red-600'
    };

    return (
        <div>
            <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-800">Usuarios</h2>
                {can('usuarios', 'create') && (
                <button
                    onClick={openCreate}
                    className="text-white text-sm font-semibold px-5 py-2.5 rounded-lg"
                    style={{ backgroundColor: 'var(--color-primary)' }}
                >
                    + Nuevo usuario
                </button>
                )}
            </div>

            {success && <div className="bg-green-50 border border-green-200 text-green-700 text-sm rounded-lg px-4 py-3 mb-4">{success}</div>}
            {error && <div className="bg-red-50 border border-red-200 text-red-600 text-sm rounded-lg px-4 py-3 mb-4">{error}</div>}

            {/* Buscador */}
            <div className="mb-4">
                <input
                    type="text"
                    placeholder="Buscar por nombre, email o cédula..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    className="w-full max-w-sm border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none"
                />
            </div>

            {/* Tabla */}
            <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
                {loading ? (
                    <div className="p-8 text-center text-gray-400 text-sm">Cargando...</div>
                ) : filtered.length === 0 ? (
                    <div className="p-8 text-center text-gray-400 text-sm">No se encontraron usuarios</div>
                ) : (
                    <table className="w-full text-sm">
                        <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
                            <tr>
                                <th className="px-4 py-3 text-left">Usuario</th>
                                <th className="px-4 py-3 text-left">Email</th>
                                <th className="px-4 py-3 text-left">Rol</th>
                                <th className="px-4 py-3 text-left">Estado</th>
                                <th className="px-4 py-3 text-left">Zona</th>
                                <th className="px-4 py-3 text-right">Acciones</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {filtered.map(user => (
                                <tr key={user.uuid} className="hover:bg-gray-50">
                                    <td className="px-4 py-3">
                                        <div className="flex items-center gap-3">
                                            {user.foto_url ? (
                                                <img src={user.foto_url} alt="" className="w-8 h-8 rounded-full object-cover" />
                                            ) : (
                                                <div
                                                    className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold"
                                                    style={{ backgroundColor: 'var(--color-primary)' }}
                                                >
                                                    {user.nombre?.charAt(0)}
                                                </div>
                                            )}
                                            <div>
                                                <p className="font-medium text-gray-800">{user.nombre} {user.apellido}</p>
                                                {user.cedula && <p className="text-xs text-gray-400">{user.cedula}</p>}
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-4 py-3 text-gray-600">{user.email}</td>
                                    <td className="px-4 py-3">
                                        <span className="px-2 py-0.5 rounded-full text-xs text-white" style={{ backgroundColor: 'var(--color-secondary)' }}>
                                            {user.role}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3">
                                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${estadoColor[user.estado]}`}>
                                            {user.estado}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 text-gray-500">{user.zona || '—'}</td>
                                    <td className="px-4 py-3 text-right">
                                        {can('usuarios', 'update') && (
                                        <button
                                            onClick={() => openEdit(user)}
                                            className="text-blue-600 hover:underline text-xs mr-3"
                                        >
                                            Editar
                                        </button>
                                        )}
                                        {can('usuarios', 'delete') && user.estado === 'activo' && (
                                            <button
                                                onClick={() => handleDeactivate(user.uuid)}
                                                className="text-red-500 hover:underline text-xs"
                                            >
                                                Desactivar
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Modal */}
            {modalOpen && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                            <h3 className="text-lg font-bold text-gray-800">
                                {editing ? 'Editar usuario' : 'Nuevo usuario'}
                            </h3>
                            <button onClick={() => setModalOpen(false)} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
                        </div>

                        <div className="p-6 space-y-4">
                            {error && <div className="bg-red-50 border border-red-200 text-red-600 text-sm rounded-lg px-4 py-3">{error}</div>}

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Nombre *</label>
                                    <input name="nombre" value={form.nombre} onChange={handleChange}
                                        className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Apellido *</label>
                                    <input name="apellido" value={form.apellido} onChange={handleChange}
                                        className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none" />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                                <input name="email" type="email" value={form.email} onChange={handleChange}
                                    className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none" />
                            </div>

                            {!editing && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Contraseña *</label>
                                    <input name="password" type="password" value={form.password} onChange={handleChange}
                                        className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none"
                                        placeholder="Mínimo 8 caracteres, mayúscula, número y especial" />
                                </div>
                            )}

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Cédula</label>
                                    <input name="cedula" value={form.cedula} onChange={handleChange}
                                        className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Teléfono</label>
                                    <input name="telefono" value={form.telefono} onChange={handleChange}
                                        className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none" />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Zona</label>
                                    <input name="zona" value={form.zona} onChange={handleChange}
                                        className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Rol *</label>
                                    <select name="role_id" value={form.role_id} onChange={handleChange}
                                        className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none">
                                        <option value="">Seleccionar...</option>
                                        {roles.map(r => (
                                            <option key={r.id} value={r.id}>{r.nombre}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {editing && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Estado</label>
                                    <select name="estado" value={form.estado} onChange={handleChange}
                                        className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none">
                                        <option value="activo">Activo</option>
                                        <option value="inactivo">Inactivo</option>
                                        <option value="suspendido">Suspendido</option>
                                    </select>
                                </div>
                            )}

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Fecha expiración <span className="text-gray-400 font-normal">(solo invitados)</span>
                                </label>
                                <input name="fecha_expiracion" type="date" value={form.fecha_expiracion} onChange={handleChange}
                                    className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none" />
                            </div>
                        </div>

                        <div className="px-6 pb-6 flex gap-3">
                            <button
                                onClick={() => setModalOpen(false)}
                                className="flex-1 border border-gray-300 text-gray-700 font-semibold py-2.5 rounded-lg text-sm"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={handleSubmit}
                                disabled={saving}
                                className="flex-1 text-white font-semibold py-2.5 rounded-lg text-sm disabled:opacity-60"
                                style={{ backgroundColor: 'var(--color-primary)' }}
                            >
                                {saving ? 'Guardando...' : editing ? 'Guardar cambios' : 'Crear usuario'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Users;
